import { useState, useEffect, useReducer } from "react";
import { Button, Card, Col, Row } from "reactstrap";
import DataGrid from "../../components/dataGrid/DataGrid";
import { Link, useLocation, useNavigate } from "react-router-dom";
import QueryString from "qs";
import moment from "moment";
import { Check, Edit, Download } from "react-feather";
import { PickListData, exportExcelPickList, getPickLists } from "../../services/pickList.service";
import toast from "react-hot-toast";
import FormInput from "../../components/FormInput";
import Dot from "../../components/Dot";
import FormSelect from "../../components/FormSelect";
import { getParties } from "../../services/parties.service";

type Status = "HOLD" | "PENDING" | "STARTED" | "STOPPED";

function mapStatus(status: Status): string {
  /* eslint-disable */
  const statusMapping: { [key in Status]: string } = {
    HOLD: "Pending",
    PENDING: "Pending",
    STARTED: "In Process",
    STOPPED: "Complete",
  };

  return statusMapping[status] || "Unknown";
  /* eslint-disable */
}

const initialState = {
  data: [],
  page: 1,
  limit: 10,
  totalPages: 1,
  totalResults: 0,
  startIndex: 1,
  endIndex: 10,
};

function reducer(state: any, action: any) {
  switch (action.type) {
    case "SET_DATA":
      return action.payload;
    default:
      return state;
  }
}

function PickLists() {
  const [filterData, setFilterData] = useState({
    party: "",
    orderNumber: "",
    workingStatus: "",
    date: "",
  });
  const [party, setParty] = useState("");
  const [workingStatus, setWorkingStatus] = useState("");
  const [data, dispatch] = useReducer(reducer, initialState);
  const location = useLocation();
  const navigate = useNavigate();
  const query = QueryString.parse(location.search, { ignoreQueryPrefix: true });

  const columns = [
    {
      selector: (row: any) => row.serialNumber,
      name: "S.No.",
      sortable: false,
      //   wrap: true,
      maxWidth: "70px",
      minWidth: "70px",
    },

    {
      selector: (row: any) => (
        <div className={`${row.U ? "text-danger" : ""}`}>
          <div>{row.party?.name || ""}</div>
          <div>({row.party?.code || ""})</div>
        </div>
      ),
      name: "Party Name",
      sortable: false,
      wrap: true,
    },

    {
      selector: (row: PickListData) => (
        <div className={`${row.U ? "text-danger" : ""}`}>{row.orderNumber || ""}</div>
      ),
      name: "Order No.",
      sortable: false,
      wrap: true,
    },
    {
      selector: (row: any) => moment(row.date).format("DD/MM/YYYY"),
      name: "Order Date",
      sortable: false,
      wrap: true,
    },

    {
      selector: (row: PickListData) => row.orderAmount,
      name: "Order Amount",
      sortable: false,
      wrap: true,
    },
    {
      selector: (row: PickListData) => row.lineItem,
      name: "Item",
      sortable: false,
      wrap: true,
    },
    {
      selector: (row: PickListData) => mapStatus(row.status),
      name: "Working Status",
      sortable: false,
      wrap: true,
    },
    {
      selector: (row: PickListData) => {
        return <>{row.VOR ? <Check strokeWidth={3} className='text-success' /> : ""}</>;
      },
      name: "VOR",
      sortable: false,
      wrap: true,
      maxWidth: "65px",
      minWidth: "65px",
    },
    {
      selector: (row: PickListData) => <Dot color={row.U ? "green" : "red"} />,
      name: "Urgent",
      sortable: false,

      maxWidth: "90px",
      minWidth: "90px",
      center: true,
    },

    {
      selector: (row: any) => (
        <>
          {row.s3Key && row.pdfDownloadUrl && (
            <button
              className='p-0 pb-1 me-1 btn btn-link'
              onClick={() => window.open(row.pdfDownloadUrl, "_blank")}
              title='Download PDF'
            >
              <Download size={15} />
            </button>
          )}
          <Link
            className='me-1'
            to={`edit/${row.id}?${QueryString.stringify({
              redirect: location.pathname + location.search,
            })}`}
          >
            <Edit size={15} />
          </Link>
        </>
      ),
      name: "Actions",
      sortable: false,
      right: true,
      maxWidth: "130px",
      minWidth: "130px",
    },
  ];

  const setData = (data: any) => {
    dispatch({ type: "SET_DATA", payload: data });
  };
  useEffect(() => {
    const { page, party, orderNumber, workingStatus, date } = query;
    getPickLists({ page, party, orderNumber, workingStatus, date })
      .then((response: any) => {
        setData(response.data);
      })
      .catch((error) => {});
  }, [location.search]);

  const handleFilter = () => {
    const queryParams: any = { ...query, page: 1, ...filterData };

    Object.keys(queryParams).forEach((key) => {
      if (!queryParams[key]) {
        delete queryParams[key];
      }
    });

    const queryString = QueryString.stringify(queryParams);
    navigate(`${location.pathname}${Object.keys(queryParams).length > 0 ? "?" + queryString : ""}`);
  };
  const handleClearFilter = () => {
    setWorkingStatus("");
    setFilterData({ party: "", orderNumber: "", workingStatus: "", date: "" });
    const queryParams = { page: 1 };
    const queryString = QueryString.stringify(queryParams);
    navigate(`${location.pathname}?${queryString}`);
  };
  return (
    <div className='d-flex flex-column h-100 px-sm-1 w-100 pb-sm-1'>
      <Row className='align-items-center mb-2'>
        <Col sm>
          <h1 className='my-1 fw-bold h4'>Pick List</h1>
        </Col>
        <Col sm='auto'>
          <div className='justify-content-between'>
            <Link
              to={`/pick-lists/add?${QueryString.stringify({
                redirect: location.pathname + location.search,
              })}`}
              className='btn btn-sm btn-primary my-1 text-white'
            >
              Add New
            </Link>
          </div>
        </Col>
      </Row>

      <div className='flex-grow-1 overflow-auto'>
        <Card className='h-100 p-3 w-100 '>
          <div className='d-flex flex-column h-100 rounded-0'>
            <DataGrid
              columns={columns}
              data={data}
              filterByDate={true}
              FilterSubHeader={true}
              filterData={filterData}
              setFilterData={setFilterData}
              subHeaderComponent={
                <Row>
                  <Col md={2}>
                    <FormSelect
                      id='party'
                      name='party'
                      label='Party'
                      isAsync
                      isClearable
                      defaultFilter={{ status: true }}
                      getOptions={getParties}
                      onChange={(name: any, value: any) => {
                        setParty(value);
                        setFilterData({
                          ...filterData,
                          [name]: value?.id,
                        });
                      }}
                      getOptionLabel={(row: any) => (
                        <>
                          <small>{row.name}</small>
                          <div>({row.code})</div>
                        </>
                      )}
                      value={party}
                    />
                  </Col>
                  <Col md={2}>
                    <FormInput
                      name='orderNumber'
                      label='Order No.'
                      placeholder='Order No.'
                      onChange={(e) =>
                        setFilterData({
                          ...filterData,
                          [e.target.name]: e.target.value,
                        })
                      }
                      value={filterData.orderNumber}
                    />
                  </Col>
                  <Col md={2}>
                    <FormSelect
                      name='workingStatus'
                      label='Working Status'
                      options={[
                        { id: "Pending", name: "Pending" },
                        { id: "In Process", name: "In Process" },
                        { id: "Complete", name: "Complete" },
                      ]}
                      isClearable
                      value={workingStatus}
                      onChange={(name: any, value: any) => {
                        setWorkingStatus(value);
                        setFilterData({
                          ...filterData,
                          [name]: value?.id,
                        });
                      }}
                    />
                  </Col>
                  <Col md={2}>
                    <FormInput
                      name='date'
                      label='Date'
                      type='date'
                      onChange={(e) =>
                        setFilterData({
                          ...filterData,
                          [e.target.name]: e.target.value,
                        })
                      }
                      value={filterData.date}
                    />
                  </Col>

                  <Col md={2} className='mt-1'>
                    <div className='mt-4'>
                      <Button
                        color='primary'
                        size='sm'
                        className='me-2'
                        onClick={() => handleFilter()}
                      >
                        Filter
                      </Button>
                      <Button
                        color='danger'
                        size='sm'
                        className='me-2'
                        onClick={() => handleClearFilter()}
                      >
                        Clear
                      </Button>
                    </div>
                  </Col>
                  <Col md={2} className='mt-1'>
                    <div className='mt-4 text-end'>
                      <Button
                        color='primary'
                        size='sm'
                        onClick={() => {
                          const { party, orderNumber, workingStatus, date } = query;

                          exportExcelPickList({ party, orderNumber, workingStatus, date })
                            .then((response) => {
                              const blobURL = URL.createObjectURL(response.data);
                              const anchor = document.createElement("a");
                              anchor.href = blobURL;
                              anchor.download = "Pick List.xlsx";
                              anchor.click();
                              URL.revokeObjectURL(blobURL);
                            })
                            .catch(() => {
                              toast.error("Record not found to export.");
                            });
                        }}
                      >
                        Export
                      </Button>
                    </div>
                  </Col>
                </Row>
              }
            />
          </div>
        </Card>
      </div>
    </div>
  );
}

export default PickLists;
